import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useGoal } from "@/queries/goals.query.ts";
import { ApiGoal, ApiGoalShort, ApiGoalStatus } from "@/types/goals.types.ts";
import {
  Box,
  FontAwesomeIcon,
  LinkTo,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface GoalBreadcrumbsProps {
  goal: ApiGoalShort;
  clickable?: boolean;
  onClick?: (goal: ApiGoal) => void;
  target?: React.HTMLAttributeAnchorTarget;
}

const MAX_TITLE_LENGTH = 30;

const flattenParentChain = (chain: ApiGoal | null): ApiGoal[] => {
  const result: ApiGoal[] = [];
  let current = chain;
  while (current) {
    result.push(current);
    current = current.parent_goal ?? null;
  }
  return result;
};

interface BreadcrumbItemProps {
  goal: ApiGoal;
  clickable: boolean;
  onClick?: (goal: ApiGoal) => void;
  target?: React.HTMLAttributeAnchorTarget;
}

function GoalCardBreadcrumbItem({ goal, clickable, onClick, target }: BreadcrumbItemProps) {
  const { truncate } = useFormatter();
  const text = truncate(goal.title, MAX_TITLE_LENGTH);
  const isTruncated = text !== goal.title;
  const isInactive = goal.status !== ApiGoalStatus.active;

  const content = (
    <Text
      className={twMerge(
        "text-secondary",
        isInactive && "line-through",
        clickable && "hover:underline"
      )}
    >
      {text}
    </Text>
  );

  const wrappedContent = isTruncated ? (
    <Tooltip>
      <TooltipTrigger>{content}</TooltipTrigger>
      <TooltipContent>{goal.title}</TooltipContent>
    </Tooltip>
  ) : (
    content
  );

  if (!clickable) {
    return wrappedContent;
  }

  const linkTo = `/goals/${goal.id}`;

  if (target) {
    return (
      <Link to={linkTo} target={target} className="no-underline">
        {wrappedContent}
      </Link>
    );
  }

  if (onClick) {
    return (
      <Box
        className="cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick(goal);
        }}
      >
        {wrappedContent}
      </Box>
    );
  }

  return (
    <LinkTo to={linkTo} className="no-underline">
      {wrappedContent}
    </LinkTo>
  );
}

export function GoalCardBreadcrumbs({
  goal,
  clickable = true,
  onClick,
  target,
}: GoalBreadcrumbsProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const parentGoal = goal.parent_goal;
  const underParentGoalId = parentGoal?.parent_goal_id;

  const { data: fullParentGoal, isLoading } = useGoal(goal.id, isExpanded);

  if (!parentGoal) {
    return null;
  }

  const handleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(true);
  };

  const parentChain =
    isExpanded && fullParentGoal ? flattenParentChain(fullParentGoal.parent_goal_chain) : [];

  const allGoals = isExpanded && fullParentGoal ? parentChain : [parentGoal];

  return (
    <Stack
      className="pb-4 border-b border-dark/5 mb-3 text-secondary flex-wrap"
      direction="horizontal"
      items="center"
      gap="sm"
    >
      <FontAwesomeIcon icon="fa-light fa-arrow-turn-right" />

      {allGoals.map((g, index) => (
        <Stack key={g.id} direction="horizontal" items="center" gap="xs">
          {index > 0 && <FontAwesomeIcon icon="fa-light fa-arrow-right" />}
          <GoalCardBreadcrumbItem
            goal={g}
            clickable={clickable}
            onClick={onClick}
            target={target}
          />
        </Stack>
      ))}

      {isLoading && (
        <>
          <FontAwesomeIcon icon="fa-light fa-arrow-right" />
          <Skeleton className="w-32 h-5" />
        </>
      )}

      {!isExpanded && !!underParentGoalId && (
        <>
          <FontAwesomeIcon icon="fa-light fa-arrow-right" />
          <Tooltip>
            <TooltipTrigger>
              <Text
                className="text-secondary cursor-pointer hover:underline"
                onClick={handleExpand}
              >
                {t("common:more")}...
              </Text>
            </TooltipTrigger>
            <TooltipContent>{t("goals:expand_parent_chain")}</TooltipContent>
          </Tooltip>
        </>
      )}
    </Stack>
  );
}
